/*
  Ever Leaf Product Section — MASTER REBUILD custom Gallery.

  Fully custom (not Dawn) Gallery: one main image + a horizontal
  thumbnail rail + prev/next arrows, driven by real product.media (see
  snippets/ever-leaf-product-gallery.liquid for the markup this queries).
  Purely presentational/navigational — never touches
  window.EverLeafPersonalizerState, the canonical seven properties, or any
  purchase-flow code. The Gallery<->Visualizer swap itself is still owned
  by assets/ever-leaf-personalizer.js (the `.ever-leaf-has-photo` class on
  `.ever-leaf-product`) — this file only manages which Gallery image is
  active while the Gallery itself is visible.
*/
(function () {
  'use strict';

  function EverLeafProductGallery(root) {
    var mainWrap = root.querySelector('[data-ever-leaf-gallery-main]');
    // NOTE (found via real Playwright/browser inspection, not guessed):
    // Shopify's `image_tag` filter does not reliably emit an arbitrary
    // hyphenated `data-*` keyword argument as a real HTML attribute — the
    // images themselves rendered correctly, but the data-attribute never
    // appeared, so this selector previously matched zero elements and no
    // image ever received `.is-active` (all stayed at the CSS default
    // opacity:0 — a blank-looking Gallery despite real, loaded images).
    // `image_tag`'s `class:` argument DOES work reliably, so the class
    // this same markup already carries is used instead — no Liquid
    // change needed.
    var images = Array.prototype.slice.call(root.querySelectorAll('.ever-leaf-product-gallery__main-image'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-ever-leaf-gallery-thumb]'));
    /*
      Gallery Mobile Nav brief, Section 2 — Mobile now renders a SECOND
      real Prev/Next pair (the overlay-over-Main-Image buttons), CSS-
      toggled against the original rail-row pair exactly like the
      Visualizer's Mobile Horizontal controls already do (at most one
      pair is ever visible/focusable per breakpoint). querySelectorAll +
      forEach wires every matched button, same safe pattern already
      proven for that case — a single querySelector here would leave
      whichever pair is NOT first in the DOM permanently dead.
    */
    var prevButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ever-leaf-gallery-prev]'));
    var nextButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ever-leaf-gallery-next]'));
    var rail = root.querySelector('[data-ever-leaf-gallery-rail]');

    if (!mainWrap || images.length === 0) return;

    var activeIndex = 0;
    var autoplayEnabled = root.getAttribute('data-autoplay') === 'true';
    var autoplayIntervalMs = parseInt(root.getAttribute('data-autoplay-interval'), 10) || 10000;
    var autoplayTimer = null;
    var userInteracted = false;

    function setActive(index) {
      if (index < 0 || index >= images.length) return;
      activeIndex = index;
      images.forEach(function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (rail && thumbs[index]) {
        var thumbEl = thumbs[index];
        var railRect = rail.getBoundingClientRect();
        var thumbRect = thumbEl.getBoundingClientRect();
        if (thumbRect.left < railRect.left || thumbRect.right > railRect.right) {
          thumbEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
      prevButtons.forEach(function (btn) { btn.disabled = false; }); // arrows always usable (wrap-around)
      nextButtons.forEach(function (btn) { btn.disabled = false; });
    }

    function goTo(index, isManual) {
      var next = ((index % images.length) + images.length) % images.length;
      setActive(next);
      if (isManual) stopAutoplay();
    }

    function next() {
      goTo(activeIndex + 1, false);
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        goTo(i, true);
      });
    });
    prevButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(activeIndex - 1, true);
      });
    });
    nextButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(activeIndex + 1, true);
      });
    });

    // ---- Mobile Gallery Swipe (PHASE 47D Part A). Reuses goTo()/
    // markManualInteraction() above — the SAME state Prev/Next buttons
    // use, no second active-index. Touch Events only (no Pointer Events,
    // no desktop mouse drag — not requested), gated to this project's
    // existing 749px mobile breakpoint at gesture-start time. Vertical
    // scroll stays fully native: every listener is {passive:true} and
    // none ever call preventDefault; touch-action:pan-y on
    // [data-ever-leaf-gallery-main] (assets/ever-leaf-product.css) is what
    // actually keeps native vertical panning available to the browser.
    // Direction is PHYSICALLY locked, matching the existing Prev/Next
    // arrows' own documented physical (non-RTL-relative) semantics
    // (see the .ever-leaf-product-gallery__rail-row comment in
    // assets/ever-leaf-product.css): swipe left is physically "next",
    // swipe right is physically "previous", identical in LTR and RTL, so
    // it can never contradict what the buttons already mean. Decided once
    // at touchend (Section 11 of the brief), never per-move, so one swipe
    // is always exactly one media change. ----
    var GALLERY_SWIPE_MOBILE_QUERY = '(max-width: 749px)';
    var GALLERY_SWIPE_MIN_DELTA_X = 40;
    var GALLERY_SWIPE_DOMINANCE_RATIO = 1.2;
    var swipeTouchStartX = 0;
    var swipeTouchStartY = 0;
    var swipeTracking = false;

    function isGallerySwipeViewport() {
      return !!(window.matchMedia && window.matchMedia(GALLERY_SWIPE_MOBILE_QUERY).matches);
    }

    mainWrap.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1 || !isGallerySwipeViewport()) {
        swipeTracking = false;
        return;
      }
      swipeTracking = true;
      swipeTouchStartX = e.touches[0].clientX;
      swipeTouchStartY = e.touches[0].clientY;
    }, { passive: true });

    mainWrap.addEventListener('touchmove', function (e) {
      // Intentionally no preventDefault and no per-move index changes here
      // — vertical scroll stays fully native, and a second touch joining
      // mid-gesture cancels swipe tracking (Section 13 — one-finger only).
      if (e.touches.length !== 1) swipeTracking = false;
    }, { passive: true });

    mainWrap.addEventListener('touchend', function (e) {
      if (!swipeTracking) return;
      swipeTracking = false;
      var touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return;
      var deltaX = touch.clientX - swipeTouchStartX;
      var deltaY = touch.clientY - swipeTouchStartY;
      var absDeltaX = Math.abs(deltaX);
      var absDeltaY = Math.abs(deltaY);
      if (absDeltaX < GALLERY_SWIPE_MIN_DELTA_X || absDeltaX <= absDeltaY * GALLERY_SWIPE_DOMINANCE_RATIO) {
        return; // too small, or not clearly horizontal (Section 10) — do nothing
      }
      if (deltaX < 0) {
        goTo(activeIndex + 1, true); // physical swipe left -> Next
      } else {
        goTo(activeIndex - 1, true); // physical swipe right -> Previous
      }
      markManualInteraction();
    }, { passive: true });

    mainWrap.addEventListener('touchcancel', function () {
      swipeTracking = false;
    }, { passive: true });

    // ---- Autoplay (Section 16) — calm, respects prefers-reduced-motion,
    // pauses on hover/focus/hidden-tab, stops after manual interaction or
    // once the Visualizer takes over from the Gallery. ----
    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function startAutoplay() {
      if (!autoplayEnabled || prefersReducedMotion || userInteracted || images.length <= 1) return;
      stopAutoplay();
      autoplayTimer = setInterval(function () {
        if (document.hidden) return;
        var productRoot = root.closest('.ever-leaf-product');
        if (productRoot && productRoot.classList.contains('ever-leaf-has-photo')) {
          stopAutoplay();
          return;
        }
        next();
      }, autoplayIntervalMs);
    }
    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }
    function pauseAutoplay() {
      stopAutoplay();
    }
    function resumeAutoplayIfIdle() {
      if (!userInteracted) startAutoplay();
    }

    function markManualInteraction() {
      userInteracted = true;
      stopAutoplay();
    }
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', markManualInteraction);
    });
    prevButtons.forEach(function (btn) { btn.addEventListener('click', markManualInteraction); });
    nextButtons.forEach(function (btn) { btn.addEventListener('click', markManualInteraction); });

    root.addEventListener('mouseenter', pauseAutoplay);
    root.addEventListener('mouseleave', resumeAutoplayIfIdle);
    root.addEventListener('focusin', pauseAutoplay);
    root.addEventListener('focusout', resumeAutoplayIfIdle);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopAutoplay();
      } else {
        resumeAutoplayIfIdle();
      }
    });

    // Stop autoplay the moment the Gallery<->Visualizer state flips to
    // "has photo" (Gallery becomes hidden) — observed via the same
    // .ever-leaf-has-photo class assets/ever-leaf-personalizer.js already
    // toggles; this file never sets that class itself.
    var productRootEl = root.closest('.ever-leaf-product');
    if (productRootEl && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (productRootEl.classList.contains('ever-leaf-has-photo')) {
          stopAutoplay();
        }
      });
      mo.observe(productRootEl, { attributes: true, attributeFilter: ['class'] });
    }

    setActive(0);
    startAutoplay();
  }

  function init() {
    var galleries = document.querySelectorAll('[data-ever-leaf-product-gallery]');
    galleries.forEach(function (root) {
      new EverLeafProductGallery(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
